import { useCallback, useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFetch } from "@/hooks/useFetch";
import { AccessImportButton } from "@/components/admin/AccessImport";
import { RoleChangeReview } from "@/components/admin/RoleChangeReview";

// Module x action grid for one role at a time; ticks are staged, reviewed as plain gains / losses, then applied.
export function PermissionsPanel() {
  const { data: roles, reload } = useFetch("/roles");
  const { data: cat } = useFetch("/permissions");
  const [key, setKey] = useState("operations");
  const [draft, setDraft] = useState(null);
  const [review, setReview] = useState(false);
  const closeReview = useCallback(() => setReview(false), []);
  const dirty = !!draft;
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  if (!roles || !cat) return null;
  const role = roles.find((r) => r.key === key) || roles[0];
  const actions = Object.keys(cat.actions);
  const cur = draft || role.permissions;
  const changed = draft ? new Set([...draft.filter((p) => !role.permissions.includes(p)), ...role.permissions.filter((p) => !draft.includes(p))]) : new Set();
  const save = (next) => setDraft(next);
  const pickRole = (k) => {
    if (changed.size && !window.confirm(`Discard ${changed.size} unsaved permission change(s) for ${role.name}?`)) return;
    setDraft(null);
    setKey(k);
  };
  const busy = false;
  const toggle = (p) => save(cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  const row = (m, on) => save(on ? [...new Set([...cur, ...m.actions.map((a) => a.perm)])] : cur.filter((p) => !p.startsWith(`${m.key}.`)));
  const used = actions.filter((a) => cat.modules.some((m) => m.actions.some((x) => x.key === a)));
  const modules = role.locked ? cat.modules : cat.modules.filter((m) => m.key !== "admin");
  return (
    <div data-testid="admin-permissions">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Select value={role.key} onValueChange={pickRole}><SelectTrigger className="w-64 bg-white" data-testid="perm-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}{r.active ? "" : " (inactive)"}</SelectItem>)}</SelectContent></Select>
        <span className="text-xs text-slate-500">{role.locked ? "Admin always has every permission (prevents lock-out)." : `${role.permissions.length} permissions · ${role.users} active user(s)`}</span>
        <span className="ml-auto"><AccessImportButton onDone={reload} /></span>
      </div>
      {changed.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="perm-unsaved-bar">
          <span><b>{changed.size} unsaved change(s)</b> for {role.name} - not live yet.</span>
          <Button size="sm" className="ml-auto" onClick={() => setReview(true)} data-testid="perm-review-btn">Review changes</Button>
          <Button size="sm" variant="outline" onClick={() => setDraft(null)} data-testid="perm-discard-btn">Discard</Button>
        </div>
      )}
      {review && <RoleChangeReview role={role} draft={draft} onClose={closeReview} onApplied={() => { setReview(false); setDraft(null); reload(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-auto">
        <table className="w-full text-xs" data-testid="perm-matrix">
          <thead className="bg-slate-100"><tr><th className="px-3 py-2 text-left">Module</th>{used.map((a) => <th key={a} className="px-2 py-2 text-center whitespace-nowrap">{cat.actions[a]}</th>)}<th className="px-2 py-2">All</th></tr></thead>
          <tbody>{modules.map((m) => {
            const all = m.actions.every((a) => cur.includes(a.perm));
            return (
              <tr key={m.key} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{m.label}</td>
                {used.map((a) => { const p = m.actions.find((x) => x.key === a); return <td key={a} className={`text-center ${p && changed.has(p.perm) ? "bg-amber-100" : ""}`}>{!p ? <span className="text-slate-200">—</span> : role.locked ? <Lock className="inline h-3 w-3 text-slate-400" /> :
                  <input type="checkbox" disabled={busy} className="h-4 w-4 accent-sky-700" checked={cur.includes(p.perm)} onChange={() => toggle(p.perm)} data-testid={`perm-${role.key}-${p.perm}`} />}</td>; })}
                <td className="text-center">{!role.locked && <input type="checkbox" disabled={busy} className="h-4 w-4 accent-slate-700" checked={all} onChange={() => row(m, !all)} data-testid={`perm-row-${role.key}-${m.key}`} />}</td>
              </tr>);
          })}</tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">Cancel / deactivate replaces delete — records are never erased. Export controls download buttons. Project Hub ticks are ready for when that module is built. The Admin module (users, roles, configuration, audit) is only for people holding the Admin role.</p>
    </div>
  );
}
